import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../utils/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { createMissingUserDocument, fixMissingAccountStatus } from '../utils/fixUserDocument';
import { useAuth } from '../contexts/authContext';
import { needsStripeConnectSetup } from '../utils/stripeConnectStatus';
import { ADMIN_USER_IDS, ADMIN_EMAILS } from '../utils/adminUtils';
import Logo from '../components/Logo';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, ready, claims, isAdmin, role } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const did = useRef(false);

  // Clean role-based redirect with Stripe Connect check
  useEffect(() => {
    if (!ready || did.current || !user) return;
    // Don't redirect if already on an admin route
    if (window.location.pathname.startsWith('/admin')) return;
    did.current = true;
    
    const checkAndRedirect = async () => {
      try {
        // Get user data to check Stripe Connect status
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        
        // Priority 1: Admin users → Admin Dashboard
        // Check admin status directly (same logic as authContext) to avoid race conditions
        const isAdminByClaims = claims?.admin === true || role === "admin";
        const isAdminByUid = ADMIN_USER_IDS.includes(user.uid);
        const isAdminByEmail = user.email && ADMIN_EMAILS.includes(user.email);
        const isAdminByFirestore = userData?.role === "admin";
        const isActuallyAdmin = isAdmin || isAdminByClaims || isAdminByUid || isAdminByEmail || isAdminByFirestore;
        
        if (isActuallyAdmin) {
          console.log('🔐 Admin user detected, redirecting to admin dashboard', {
            isAdmin,
            isAdminByClaims,
            isAdminByUid,
            isAdminByEmail,
            isAdminByFirestore
          });
          navigate("/admin", { replace: true });
          return;
        }
        
        // Priority 2: League users (team_captain, team_member) → Team Dashboard
        // Check both Firestore role and claims role for league users
        const isLeagueUser = role === 'team_captain' || 
                            role === 'team_member' || 
                            claims?.role === 'league_user' ||
                            userData?.role === 'team_captain' ||
                            userData?.role === 'team_member';
        
        if (isLeagueUser) {
          console.log('🏆 League user detected, redirecting to team dashboard');
          navigate("/team/dashboard", { replace: true });
          return;
        }
        
        // Priority 3: Host users → Host Dashboard (with Stripe Connect check)
        // Check both Firestore role and claims for hosts
        const isHost = role === 'host' || 
                      claims?.host || 
                      claims?.role === 'host' ||
                      userData?.role === 'host' ||
                      !userData?.role; // Default to host if no role set
        
        if (isHost) {
          // Check if host needs Stripe Connect setup
          if (needsStripeConnectSetup(userData, userData?.role || 'host')) {
            console.log('💳 Host needs Stripe Connect setup');
            navigate("/stripe-connect-onboarding", { replace: true });
          } else {
            console.log('🎮 Host user detected, redirecting to host dashboard');
            navigate("/home", { replace: true });
          }
          return;
        }
        
        // Default fallback: Host Dashboard
        console.log('⚠️ Unknown role, defaulting to host dashboard');
        navigate("/home", { replace: true });
      } catch (error) {
        console.error('Error checking user data for redirect:', error);
        // Fallback to default redirect
        navigate("/home", { replace: true });
      }
    };
    
    checkAndRedirect();
  }, [ready, user, claims, isAdmin, role, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if email is verified in Firebase Auth
      if (!user.emailVerified) {
        // SECURITY: No bypasses allowed - email verification is mandatory
        setError('Please verify your email address before signing in. Check your inbox for a verification email.');
        await auth.signOut();
        setLoading(false);
        return;
      }
      
      // Fetch user role from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      
      // Fix missing accountStatus field if needed
      if (userData && !userData.accountStatus) {
        await fixMissingAccountStatus(user.uid);
      }
      
      // Check if email is verified in our database
      if (userData && !userData.emailVerified) {
        // If Firebase Auth says email is verified but our database doesn't, update it
        if (user.emailVerified) {
          console.log('🔄 Syncing email verification status...');
          try {
            await updateDoc(doc(db, 'users', user.uid), {
              emailVerified: true,
              emailVerifiedAt: new Date()
            });
            console.log('✅ Email verification status synced');
          } catch (syncError) {
            console.error('❌ Error syncing email verification:', syncError);
            // Continue with login even if sync fails
          }
        } else {
          setError('Please verify your email address before signing in. Check your inbox for a verification email.');
          await auth.signOut();
          setLoading(false);
          return;
        }
      } else if (userData && userData.emailVerified && !user.emailVerified) {
        // If our database says verified but Firebase Auth doesn't, sync to Firebase Auth
        console.log('🔄 Syncing Firebase Auth email verification status...');
        // Note: We can't directly update Firebase Auth emailVerified status
        // The user will need to click the verification link again
      }
      
      // If user document doesn't exist, create it with default host role
      if (!userData) {
        console.log('⚠️ User document missing, creating...');
        await createMissingUserDocument(user, 'host'); // Default to host, not admin
        // Fetch the newly created document
        const newUserDoc = await getDoc(doc(db, 'users', user.uid));
        const newUserData = newUserDoc.data();
        
        // Redirect will be handled by useEffect based on claims
        console.log('✅ User document created, redirect will be handled by auth state change');
      } else {
        // Redirect will be handled by useEffect based on claims
        console.log('✅ User authenticated, redirect will be handled by auth state change');
      }
      
      // Debug: Log the user data to console
      console.log('🔍 Login Debug:', {
        userEmail: user.email,
        userRole: userData?.role,
        userData: userData
      });
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle specific Firebase Auth errors
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address. Please sign up first.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later.');
      } else if (err.code === 'auth/user-disabled') {
        setError('This account has been disabled. Please contact support.');
      } else {
        setError('Invalid email or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900">
      {/* Back to Landing Page Button */}
      <div className="absolute top-4 left-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-green-400 hover:text-green-300 font-semibold transition-colors"
        >
          ← Back to Landing Page
        </button>
      </div>

      <div className="flex flex-col items-center mb-8 animate-fadein">
        <Logo size={120} className="mb-4 landing-logo-white" />
        <h1 className="text-3xl font-bold text-white mb-2">Welcome back!</h1>
        <p className="text-green-200 mb-2">Sign in to buy, sell, host or play Trivia Plug</p>
      </div>
      <form
        onSubmit={handleLogin}
        className="w-full max-w-sm bg-gray-800 rounded-2xl shadow-2xl p-8 flex flex-col gap-4 animate-fadein"
        style={{ animationDelay: '0.2s' }}
      >
        <label className="text-green-200 font-semibold">Email</label>
        <input
          type="email"
          className="p-3 rounded bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:outline-none transition"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
        />
        <label className="text-green-200 font-semibold">Password</label>
        <input
          type="password"
          className="p-3 rounded bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:outline-none transition"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
        
        {/* Forgot Password Link */}
        <div className="text-right">
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-green-400 hover:text-green-300 text-sm font-medium transition-colors"
          >
            Forgot Password?
          </button>
        </div>
        
        {error && <div className="text-red-400 text-sm text-center mt-2">{error}</div>}
        
        <button
          type="submit"
          className="mt-4 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg shadow-lg transition disabled:opacity-60"
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
        
        {/* Sign Up Link */}
        <div className="text-center mt-4">
          <p className="text-gray-400 text-sm">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="text-green-400 hover:text-green-300 font-semibold transition-colors"
            >
              Sign up here
            </button>
          </p>
        </div>
      </form>
      <style>{`
        .landing-logo-white {
          filter: brightness(0) invert(1) drop-shadow(0 0 16px #fff8);
        }
        @keyframes fadein {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: none; }
        }
        .animate-fadein {
          animation: fadein 1s cubic-bezier(0.4,0,0.2,1) both;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
